import React, { useState, useEffect, useRef } from 'react';
import { ModalWrapper, ButtonGroup } from 'components/molecules';
import { PopupProps } from 'react-popup-manager/dist/src';
import { Button, Space, message, Image, Upload } from 'antd';
import { 
    CaretRightOutlined, 
    CaretLeftOutlined, 
    FastForwardOutlined, 
    FastBackwardOutlined, 
    PrinterOutlined, 
    DownloadOutlined, 
    UploadOutlined,
    ZoomInOutlined,
    ZoomOutOutlined } from '@ant-design/icons';
import { Document, Page, pdfjs } from "react-pdf";
import axios from 'axios';
import { endpoints } from 'services';
import { SERVER_URL } from 'commons/constants';
import { useFileInfo } from 'hooks';
import {useReactToPrint} from 'react-to-print';
import { UploadFile } from 'antd/lib/upload/interface';
import type { RcFile } from 'antd/es/upload';
import { isImageFile } from 'utils/file-utils'
import './index.scss'

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

export interface FileViewerModalProps extends PopupProps {
    fileid: number;
    filename: string;
    seq?: number;
    gubun?: string;
    editable?: boolean;
}

export interface ImagePrintProps extends PopupProps {
    printRef: any;
    imageUrl: string;
}

const ImagePrint = ({printRef, imageUrl}: ImagePrintProps): React.ReactElement => {
    return (
        <img ref={printRef} src={imageUrl} />
    );
}

const FileViewerModal = ({ isOpen, onClose, fileid, filename, seq=0, gubun='', editable = false }: FileViewerModalProps): React.ReactElement => {
  /* ======================================================
   * states
   * ====================================================== */    
  const [pageNum, setPageNum] = useState<number>(1);
  const [scale, setScale] = useState<number>(100);
  const [pageSize, setPageSize] = useState<number>(1);
  const [fileUrl, setFileUrl] = useState<string>('');
  const [pdfFile, setPdfFile] = useState<boolean>(false);
  const [maxScale, setMaxScale] = useState<number>(200);
  const [uploadFileList, setUploadFileList] = useState<UploadFile[]>([]);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [viewFileName, setViewFileName] = useState<string>(filename);

  const { fileInfo } = useFileInfo({file_id: fileid});

  const pdfRef = useRef();
  const imageRef = useRef();

   /* ======================================================
   * events
   * ====================================================== */
   const onSave = () => {
    onClose?.(isEdit, seq, gubun, uploadFileList.map((file) => file.originFileObj));
   };

    const onCancel = () => {
        onClose?.(false);
    };
   
    const onPrint = () => {
        if (pdfFile)   {
            pdfPrint();
        } else {
            imagePrint();
        }
        
    };

    const onDocumentLoadSuccess = (pdf: any) => {
        setPageSize(pdf._pdfInfo.numPages);
        setScale(70);
    }

    const onDownload = (): void => {
        axios({
          url: SERVER_URL + endpoints.file.download + '?file_id=' + fileid,
          method: 'get',
          responseType: 'blob',
        })
          .then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
          })
          .catch(() => {
            message.error('파일 다운로드 중 에러가 발생했습니다.', 1);
          });
    };    

    const pdfPrint  = useReactToPrint({
        content: () => pdfRef.current || null,
        documentTitle: viewFileName
    });   
       
    const imagePrint  = useReactToPrint({
        content: () => imageRef.current || null,
        documentTitle: viewFileName
    });   
           
  /* ======================================================
   * effects
   * ====================================================== */

    useEffect(() => {
        if (fileInfo) {
            setFileUrl(SERVER_URL + fileInfo.file_url);
            if (fileInfo.file_ext.toLocaleLowerCase() === 'pdf') {
                setPdfFile(true);
                setScale(70);
                setMaxScale(200);
            } else {
                setPdfFile(false);
                setScale(100);
                setMaxScale(100);
            }
            
        }

    }, [fileInfo]);
    
    useEffect(() => {
        if (isImageFile(viewFileName)) {
            setPdfFile(false);
        } else {
            setPdfFile(true);
            setScale(70);            
        }
 
    }, [viewFileName]);

    return (
        <ModalWrapper open={isOpen} onCancel={onCancel} width={600} title={'파일뷰어 - ' + viewFileName} closable>
            <div className={'file_control_panel'}>
                <Space>
                {(pdfFile) && (
                <>
                    <Button 
                        shape="round" 
                        icon={<FastBackwardOutlined />} 
                        size='small' 
                        style={{padding:'1px',width:'24px'}}
                        onClick={() => {
                            setPageNum(1);  
                        }}
                        disabled={pageNum===1}
                    /> 
                    <Button 
                        shape="round" 
                        icon={<CaretLeftOutlined />} 
                        size='small' 
                        style={{padding:'1px',width:'24px'}}
                        onClick={() => {
                            setPageNum(pageNum-1);   
                        }}
                        disabled={pageNum===1}
                    />                                 
                    <span>{pageNum} / {pageSize}</span>
                    <Button 
                        shape="round" 
                        icon={<CaretRightOutlined />} 
                        size='small' 
                        style={{padding:'1px',width:'24px'}}
                        onClick={() => {
                            setPageNum(pageNum+1);
                        }}
                        disabled={pageNum===pageSize}
                    /> 
                    <Button 
                        shape="round" 
                        icon={<FastForwardOutlined />} 
                        size='small' 
                        style={{padding:'1px',width:'24px', marginRight:'10px'}}
                        onClick={() => {
                            setPageNum(pageSize);
                        }}
                        disabled={pageNum===pageSize}
                    /> 
                    <Button 
                        shape="round" 
                        icon={<ZoomOutOutlined />} 
                        size='small' 
                        style={{padding:'1px',width:'24px'}}
                        onClick={() => {
                            if (scale > 50) setScale(scale-10);
                        }}
                        disabled={scale===50}
                    /> 
                    <span>{scale}%</span>
                    <Button 
                        shape="round" 
                        icon={<ZoomInOutlined />} 
                        size='small' 
                        style={{padding:'1px',width:'24px', marginRight:'10px'}}
                        onClick={() => {
                            if (scale < maxScale) setScale(scale+10);
                        }}
                        disabled={scale===maxScale}
                    />                       
                </>
                )}
                </Space>
                <Space>
                    {editable && (
                        <Upload
                            fileList={uploadFileList}
                            onChange={({ fileList: list }) => {
                                setUploadFileList(list);

                                const file = list.at(0);
                                if (file) {
                                    const fileReader = new FileReader();
                                    fileReader.readAsDataURL(file.originFileObj as RcFile) 
                                    fileReader.onload = () => {
                                        setFileUrl(fileReader.result as string);                                    
                                    }

                                    setViewFileName(file.name)
                                    setIsEdit(true);
                                }
                            }} 
                            beforeUpload={(file, filelist) => {
                                if ((file.size as number) >= 10 * 1024 * 1024) {
                                    message.warn('파일 용량 초과', 1);
                                    return Upload.LIST_IGNORE;
                                }
                            }}                                                       
                            customRequest={() => true}
                            maxCount={1}
                            accept=".pdf,.jpg,.jpeg,.png"
                            showUploadList={false}
                        >
                            <Button 
                                shape="round" 
                                icon={<UploadOutlined />} 
                                size='small' 
                                style={{padding:'1px',width:'24px'}}

                            /> 
                        </Upload> 
                    )}
                    <Button 
                        shape="round" 
                        icon={<DownloadOutlined />} 
                        size='small' 
                        style={{padding:'1px',width:'24px'}}
                        onClick={onDownload}
                        disabled={isEdit}
                    /> 
                    <Button 
                        shape="round" 
                        icon={<PrinterOutlined />} 
                        size='small' 
                        style={{padding:'1px',width:'24px'}}
                        onClick={onPrint}
                    /> 
                </Space>
            </div>            
            <div className={'file_viewer'}>
            {(pdfFile) ? (
                <Document
                    file={fileUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                   >
                    <Page scale={scale*0.01} pageNumber={pageNum} renderTextLayer={false} renderAnnotationLayer={false} />
                </Document>
            ) : (
                <Image
                    src={fileUrl}
                />
            )}
            </div>
            <div className={'print_viewer'} style={{width:screen.width}}>
            {(pdfFile) ? (
                <Document
                    file={fileUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    ref={pdfRef}
                   >
                    <Page width={screen.width} pageNumber={pageNum} renderTextLayer={false} renderAnnotationLayer={false} />
                </Document>
            ) : (
                <ImagePrint printRef={imageRef} imageUrl={fileUrl} />
            )}
            </div> 
            {editable && (
                <ButtonGroup className={'buttonsWrapper'}>
                    <Button onClick={onCancel}>취소</Button>
                    <Button type="primary" disabled={!isEdit} onClick={onSave} >저장</Button>
                </ButtonGroup>                               
            )}           
        </ModalWrapper>  
    )    
};

export default React.memo(FileViewerModal);    